import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { leaderboardAPI } from '../../services/api';

// Async thunks
export const fetchLecturers = createAsyncThunk(
  'leaderboard/fetchLecturers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaderboardAPI.getLecturers();
      if (response.status === 'success') {
        return response.data;
      }
      return rejectWithValue('Failed to fetch lecturers');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch lecturers');
    }
  }
);

export const fetchStudents = createAsyncThunk(
  'leaderboard/fetchStudents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaderboardAPI.getStudents();
      if (response.status === 'success') {
        return response.data;
      }
      return rejectWithValue('Failed to fetch students');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch students');
    }
  }
);

export const fetchStudyPrograms = createAsyncThunk(
  'leaderboard/fetchStudyPrograms',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaderboardAPI.getStudyPrograms();
      if (response.status === 'success') {
        return response.data;
      }
      return rejectWithValue('Failed to fetch study programs');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch study programs');
    }
  }
);

export const fetchUnits = createAsyncThunk(
  'leaderboard/fetchUnits',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leaderboardAPI.getUnits();
      if (response.status === 'success') {
        return response.data;
      }
      return rejectWithValue('Failed to fetch units');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch units');
    }
  }
);

const initialState = {
  lecturers: [],
  students: [],
  studyPrograms: [],
  units: [],
  loading: {
    lecturers: false,
    students: false,
    studyPrograms: false,
    units: false,
  },
  error: {
    lecturers: null,
    students: null,
    studyPrograms: null,
    units: null,
  },
  lastFetch: {
    lecturers: null,
    students: null,
    studyPrograms: null,
    units: null,
  },
  selectedCategory: 'lecturers', // lecturers, students, studyPrograms, units
  searchQuery: '',
  sortBy: 'score', // score, name
  sortOrder: 'desc', // asc, desc
};

const leaderboardSlice = createSlice({
  name: 'leaderboard',
  initialState,
  reducers: {
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    clearError: (state, action) => {
      const category = action.payload;
      if (category) {
        state.error[category] = null;
      } else {
        state.error = {
          lecturers: null,
          students: null,
          studyPrograms: null,
          units: null,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Lecturers
      .addCase(fetchLecturers.pending, (state) => {
        state.loading.lecturers = true;
        state.error.lecturers = null;
      })
      .addCase(fetchLecturers.fulfilled, (state, action) => {
        state.loading.lecturers = false;
        state.lecturers = action.payload;
        state.lastFetch.lecturers = new Date().toISOString();
      })
      .addCase(fetchLecturers.rejected, (state, action) => {
        state.loading.lecturers = false;
        state.error.lecturers = action.payload;
      })
      // Fetch Students
      .addCase(fetchStudents.pending, (state) => {
        state.loading.students = true;
        state.error.students = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.loading.students = false;
        state.students = action.payload;
        state.lastFetch.students = new Date().toISOString();
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.loading.students = false;
        state.error.students = action.payload;
      })
      // Fetch Study Programs
      .addCase(fetchStudyPrograms.pending, (state) => {
        state.loading.studyPrograms = true;
        state.error.studyPrograms = null;
      })
      .addCase(fetchStudyPrograms.fulfilled, (state, action) => {
        state.loading.studyPrograms = false;
        state.studyPrograms = action.payload;
        state.lastFetch.studyPrograms = new Date().toISOString();
      })
      .addCase(fetchStudyPrograms.rejected, (state, action) => {
        state.loading.studyPrograms = false;
        state.error.studyPrograms = action.payload;
      })
      // Fetch Units
      .addCase(fetchUnits.pending, (state) => {
        state.loading.units = true;
        state.error.units = null;
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.loading.units = false;
        state.units = action.payload;
        state.lastFetch.units = new Date().toISOString();
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.loading.units = false;
        state.error.units = action.payload;
      });
  },
});

export const {
  setSelectedCategory,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  clearError,
} = leaderboardSlice.actions;

export default leaderboardSlice.reducer;
